// src/pages/CreateClient.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { apiURL } from "../config/config";
import { ClipLoader } from "react-spinners";
import BackButton from "../components/BackButton";
import ClientApproversFields from "../components/ClientApproversFields";

const emptyApprover = { name: "", email: "", position: "" };

const buildClientPayload = (data) => {
  const clientData = { ...data };
  delete clientData._id;
  delete clientData.__v;

  return {
    ...clientData,
    authorizedApprovers: (data.authorizedApprovers || [])
      .map((approver) => ({
        name: String(approver.name || "").trim(),
        email: String(approver.email || "").trim(),
        position: String(approver.position || "").trim(),
      }))
      .filter((approver) => approver.name || approver.email || approver.position),
  };
};

const CreateClient = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: "",
    companyBaseName: "",
    email: "",
    address: "",
    taxIdentificationNumber: "",
    contactPhoneNumber: "",
    branchNo: "00000",
    authorizedApprovers: [{ ...emptyApprover }],
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleApproverChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      authorizedApprovers: prev.authorizedApprovers.map((approver, approverIndex) =>
        approverIndex === index ? { ...approver, [field]: value } : approver
      ),
    }));
  };

  const handleAddApprover = () => {
    setFormData((prev) => ({
      ...prev,
      authorizedApprovers: [...prev.authorizedApprovers, { ...emptyApprover }],
    }));
  };

  const handleRemoveApprover = (index) => {
    setFormData((prev) => ({
      ...prev,
      authorizedApprovers: prev.authorizedApprovers.filter(
        (_, approverIndex) => approverIndex !== index
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${apiURL}clients`, buildClientPayload(formData), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("เพิ่มลูกค้าใหม่สำเร็จ ✅");
      navigate("/clients");
    } catch (err) {
      toast.error("เกิดข้อผิดพลาดในการเพิ่มลูกค้า ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="
        min-h-screen
        p-6
        bg-gray-100
        dark:bg-gray-950
        transition-colors
        duration-300
      "
    >
      <div
        className="
          max-w-3xl mx-auto
          p-6
          bg-white
          dark:bg-gray-900
          shadow-lg
          dark:shadow-gray-800
          rounded-lg
          transition-colors
          duration-300
        "
      >
        <div className="mb-4">
          <BackButton label="ย้อนกลับ" />
        </div>

        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-200">
          เพิ่มลูกค้าใหม่
        </h2>

        {loading ? (
          <div className="flex justify-center py-10">
            <ClipLoader size={50} color="#4F46E5" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-semibold text-gray-700 dark:text-gray-300">
                ชื่อลูกค้า (Customer Name) แสดงบนหัว QT
              </label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                required
                className="
                  w-full p-2 border rounded
                  bg-white dark:bg-gray-800
                  text-gray-800 dark:text-gray-200
                  border-gray-300 dark:border-gray-600
                "
              />
            </div>

            <div>
              <label className="font-semibold text-gray-700 dark:text-gray-300">
                ชื่อบริษัทหลัก (Company Base Name) แสดงตรงช่องลายเซ็น
              </label>
              <input
                type="text"
                name="companyBaseName"
                value={formData.companyBaseName}
                onChange={handleChange}
                required
                className="
                  w-full p-2 border rounded
                  bg-white dark:bg-gray-800
                  text-gray-800 dark:text-gray-200
                  border-gray-300 dark:border-gray-600
                "
              />
            </div>

            <div>
              <label className="font-semibold text-gray-700 dark:text-gray-300">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="client@example.com"
                className="
                  w-full p-2 border rounded
                  bg-white dark:bg-gray-800
                  text-gray-800 dark:text-gray-200
                  border-gray-300 dark:border-gray-600
                "
              />
            </div>

            <div>
              <label className="font-semibold text-gray-700 dark:text-gray-300">
                ที่อยู่ (Address)
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={3}
                className="
                  w-full p-2 border rounded
                  bg-white dark:bg-gray-800
                  text-gray-800 dark:text-gray-200
                  border-gray-300 dark:border-gray-600
                "
              />
            </div>

            <div>
              <label className="font-semibold text-gray-700 dark:text-gray-300">
                เลขประจำตัวผู้เสียภาษี (Tax ID)
              </label>
              <input
                type="text"
                name="taxIdentificationNumber"
                value={formData.taxIdentificationNumber}
                onChange={handleChange}
                className="
                  w-full p-2 border rounded
                  bg-white dark:bg-gray-800
                  text-gray-800 dark:text-gray-200
                  border-gray-300 dark:border-gray-600
                "
              />
            </div>

            <div>
              <label className="font-semibold text-gray-700 dark:text-gray-300">
                เบอร์ติดต่อ (Contact Phone)
              </label>
              <input
                type="text"
                name="contactPhoneNumber"
                value={formData.contactPhoneNumber}
                onChange={handleChange}
                placeholder="เช่น 0812345678 หรือ -"
                className="
                  w-full p-2 border rounded
                  bg-white dark:bg-gray-800
                  text-gray-800 dark:text-gray-200
                  border-gray-300 dark:border-gray-600
                "
              />
            </div>

            <div>
              <label className="font-semibold text-gray-700 dark:text-gray-300">
                Branch No.
              </label>
              <input
                type="text"
                name="branchNo"
                value={formData.branchNo}
                onChange={handleChange}
                placeholder="00000"
                className="
                  w-full p-2 border rounded
                  bg-white dark:bg-gray-800
                  text-gray-800 dark:text-gray-200
                  border-gray-300 dark:border-gray-600
                "
              />
            </div>

            <ClientApproversFields
              approvers={formData.authorizedApprovers}
              onApproverChange={handleApproverChange}
              onAddApprover={handleAddApprover}
              onRemoveApprover={handleRemoveApprover}
            />

            <button
              type="submit"
              className="
                bg-green-600 dark:bg-green-700
                text-white px-4 py-2 rounded
                hover:bg-green-700 dark:hover:bg-green-800
                transition w-full sm:w-auto
              "
            >
              สร้างลูกค้า
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateClient;
